import { supabase } from '../lib/supabase';
import { runOnlineOrToast } from '../utils/networkGuard';
import { logger } from '../utils/logger';

// Helper para fazer requisições REST ao Supabase
export const supabaseRestRequest = async (
  endpoint: string,
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  body?: any
) => {
  let accessToken = '';

  try {
    const { data } = await supabase.auth.getSession();
    accessToken = data?.session?.access_token || '';
  } catch (error) {
    logger.warn('[FamilyService] Não foi possível obter sessão ativa do Supabase para REST', error);
  }

  if (!accessToken) {
    const tokensStr = localStorage.getItem('supabase.auth.token');
    if (tokensStr) {
      try {
        const tokens = JSON.parse(tokensStr);
        accessToken = tokens.access_token;
      } catch (e) {
        logger.error('Erro ao parsear tokens do localStorage', e);
      }
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    'Prefer': 'return=representation',
    'Cache-Control': 'no-store'
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const options: RequestInit = {
    method,
    headers,
    cache: 'no-store'
  };

  if (body && (method === 'POST' || method === 'PATCH')) {
    options.body = JSON.stringify(body);
  }

  const doFetch = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/${endpoint}`,
        { ...options, signal: controller.signal }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Erro na requisição: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        throw new Error('Tempo limite excedido ao se comunicar com o servidor. Tente novamente.');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Apenas para writes exibir toast quando offline
  if (method === 'POST' || method === 'PATCH' || method === 'DELETE') {
    return runOnlineOrToast(doFetch);
  }

  return doFetch();
};

// Helper para obter user ID do token do localStorage
const getUserIdFromToken = (): string | null => {
  try {
    const tokensStr = localStorage.getItem('supabase.auth.token');
    if (tokensStr) {
      const tokens = JSON.parse(tokensStr);
      if (tokens.access_token) {
        const payload = JSON.parse(atob(tokens.access_token.split('.')[1]));
        return payload.sub;
      }
    }
    
    logger.warn('[FamilyService] Não foi possível obter user ID do token');
    return null;
  } catch (error) {
    logger.error('Erro ao obter user ID do token:', error);
    return null;
  }
};

export interface FamilyMember {
  id?: string;
  family_id?: string | null;
  user_id?: string | null;
  name: string;
  date_of_birth?: string | null;
  birth_date?: string;
  gender: 'male' | 'female' | 'other' | null;
  avatar_url: string | null;
  is_main_user?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface Family {
  id?: string;
  name: string;
  created_by?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

class FamilyService {
  private static instance: FamilyService;

  private constructor() {}

  static getInstance(): FamilyService {
    if (!FamilyService.instance) {
      FamilyService.instance = new FamilyService();
    }
    return FamilyService.instance;
  }

  // Criar família
  async createFamily(name: string): Promise<Family> {
    console.log('📝 [FamilyService] Criando família:', name);
    
    const userId = getUserIdFromToken();
    
    if (!userId) {
      throw new Error('Usuário não autenticado. Por favor, faça login novamente.');
    }

    logger.sensitive('Usuário autenticado', { userId });
    console.log('💾 [FamilyService] Inserindo família via API REST...');

    try {
      const data = await supabaseRestRequest('families?select=*', 'POST', {
        name,
        created_by: userId
      });

      logger.sensitive('[FamilyService] Família criada via REST:', data);
      return Array.isArray(data) ? data[0] : data;
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao criar família via REST:', error);
      throw new Error(`Erro ao criar família: ${error.message}`);
    }
  }

  // Buscar família do usuário
  async getUserFamily(): Promise<Family | null> {
    logger.debug('[FamilyService] Buscando família do usuário via API REST');
    
    const userId = getUserIdFromToken();
    
    if (!userId) {
      throw new Error('Usuário não autenticado');
    }

    try {
      const data = await supabaseRestRequest(
        `families?created_by=eq.${userId}&select=*`,
        'GET'
      );

      const family = Array.isArray(data) && data.length > 0 ? data[0] : null;
      logger.sensitive('[FamilyService] Família encontrada via REST:', family);
      return family;
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao buscar família via REST:', error);
      return null;
    }
  }

  // Atualizar família
  async updateFamily(familyId: string, updates: Partial<Family>): Promise<Family> {
    console.log('📝 [FamilyService] Atualizando família:', familyId);

    try {
      const payload = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      const data = await supabaseRestRequest(
        `families?id=eq.${familyId}&select=*`,
        'PATCH',
        payload
      );

      const updatedFamily = Array.isArray(data) ? data[0] : data;
      logger.sensitive('[FamilyService] Família atualizada via REST:', updatedFamily);
      return updatedFamily;
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao atualizar família via REST:', error);
      throw new Error(`Erro ao atualizar família: ${error.message}`);
    }
  }

  // Buscar família do usuário (método antigo - manter para compatibilidade)
  async getUserFamilyOld(): Promise<Family | null> {
    logger.debug('[FamilyService] Buscando família do usuário (método antigo)');
    
    // Adicionar timeout para getUser
    let user;
    try {
      const getUserPromise = supabase.auth.getUser();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout ao buscar usuário')), 5000)
      );

      const result = await Promise.race([getUserPromise, timeoutPromise]) as any;
      user = result.data?.user;
    } catch (_timeoutError) {
      logger.warn('[FamilyService] Timeout ao buscar usuário, tentando getSession...');
      logger.warn('Timeout details:', _timeoutError);
      
      try {
        const getSessionPromise = supabase.auth.getSession();
        const sessionTimeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout ao buscar sessão')), 5000)
        );

        const sessionResult = await Promise.race([getSessionPromise, sessionTimeoutPromise]) as any;
        user = sessionResult.data?.session?.user;
      } catch (_sessionError) {
        logger.error('[FamilyService] Erro ao buscar sessão:', _sessionError);
      }
    }
    
    if (!user) {
      throw new Error('Usuário não autenticado');
    }

    const { data, error } = await supabase
      .from('families')
      .select('*')
      .eq('created_by', user.id)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = not found
      logger.error('[FamilyService] Erro ao buscar família:', error);
      throw new Error(`Erro ao buscar família: ${error.message}`);
    }

    logger.sensitive('[FamilyService] Família encontrada:', data);
    return data;
  }

  // Adicionar membro à família
  async addFamilyMember(familyId: string, member: FamilyMember): Promise<FamilyMember> {
    console.log('📝 [FamilyService] Adicionando membro:', member.name);
    
    const userId = getUserIdFromToken();
    
    if (!userId) {
      throw new Error('Usuário não autenticado');
    }

    logger.debug('[FamilyService] Inserindo membro via API REST...');

    try {
      const data = await supabaseRestRequest('family_members?select=*', 'POST', {
        family_id: familyId,
        user_id: userId,
        name: member.name,
        birth_date: member.birth_date || member.date_of_birth,
        gender: member.gender,
        avatar_url: member.avatar_url,
        is_main_user: member.is_main_user || false
      });

      logger.sensitive('[FamilyService] Membro adicionado via REST:', data);
      return Array.isArray(data) ? data[0] : data;
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao adicionar membro via REST:', error);
      throw new Error(`Erro ao adicionar membro: ${error.message}`);
    }
  }

  // Buscar membros da família
  async getFamilyMembers(familyId: string): Promise<FamilyMember[]> {
    logger.sensitive('[FamilyService] Buscando membros da família:', familyId);
    
    try {
      const data = await supabaseRestRequest(
        `family_members?family_id=eq.${familyId}&order=created_at.asc&select=*`,
        'GET'
      );

      logger.debug('[FamilyService] Membros encontrados via REST:', data?.length || 0);
      return data || [];
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao buscar membros via REST:', error);
      throw new Error(`Erro ao buscar membros: ${error.message}`);
    }
  }

  // Buscar todos os membros do usuário
  async getUserFamilyMembers(): Promise<FamilyMember[]> {
    logger.debug('[FamilyService] Buscando membros da família do usuário');
    
    const family = await this.getUserFamily();
    
    if (!family) {
      logger.debug('[FamilyService] Usuário não tem família cadastrada');
      return [];
    }

    return this.getFamilyMembers(family.id!);
  }

  // Atualizar membro
  async updateFamilyMember(memberId: string, updates: Partial<FamilyMember>): Promise<FamilyMember> {
    logger.sensitive('Atualizando membro', { memberId });
    
    const data = await runOnlineOrToast(async () => {
      const { data, error } = await supabase
        .from('family_members')
        .update(updates)
        .eq('id', memberId)
        .select()
        .single();

      if (error) {
        logger.error('[FamilyService] Erro ao atualizar membro:', error);
        throw new Error(`Erro ao atualizar membro: ${error.message}`);
      }

      return data as FamilyMember;
    });

    logger.success('[FamilyService] Membro atualizado:', data);
    return data as FamilyMember;
  }

  // Deletar membro
  async deleteFamilyMember(memberId: string): Promise<void> {
    logger.sensitive('[FamilyService] Deletando membro:', memberId);
    
    await runOnlineOrToast(async () => {
      const { error } = await supabase
        .from('family_members')
        .delete()
        .eq('id', memberId);

      if (error) {
        logger.error('[FamilyService] Erro ao deletar membro:', error);
        throw new Error(`Erro ao deletar membro: ${error.message}`);
      }
    });

    logger.success('[FamilyService] Membro deletado');
  }

  // ========== MEDICAÇÕES ==========

  // Buscar medicações ativas de um membro
  async getMemberMedications(memberId: string): Promise<any[]> {
    logger.sensitive('[FamilyService] Buscando medicações do membro (v2):', memberId);
    
    try {
      // ATUALIZADO: retornar TODAS as medicações do membro (ativas, concluídas e futuras)
      // A filtragem por "ativas para o dia"/"concluídas" é responsabilidade das telas (ex.: MemberDashboard, Home).
      const data = await supabaseRestRequest(
        `medications_v2?member_id=eq.${memberId}`
        + `&order=created_at.desc`
        + `&select=`
        + [
            'id',
            'member_id',
            'remedy_name',
            'potency',
            'dosage_type',
            'quantity',
            'frequency',
            'times',
            'start_date',
            'end_date',
            'usage_days',
            'pause_days',
            'total_period_months',
            'notes',
            'is_active'
          ].join(','),
        'GET'
      );

      logger.debug('[FamilyService] Medicações v2 encontradas:', data?.length || 0);
      return data || [];
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao buscar medicações v2:', error);
      return [];
    }
  }

  // Adicionar medicação (v2)
  async addMedication(memberId: string, medication: any): Promise<any> {
    logger.sensitive('[FamilyService] Adicionando medicação v2 para membro:', memberId);
    logger.debug('[FamilyService] Dados recebidos:', medication);
    
    try {
      // ATUALIZADO: Usar medications_v2 (SEM remedy_id - coluna não existe)
      const payload = {
        member_id: memberId,
        remedy_name: medication.remedy_name,
        potency: medication.potency,
        dosage_type: medication.dosage_type, // 'globules' ou 'drops'
        quantity: medication.quantity || parseInt(medication.dosage), // Quantidade
        frequency: medication.frequency,
        times: medication.times, // Array de horários
        start_date: medication.start_date,
        end_date: medication.end_date,
        usage_days: medication.usage_days || 30,
        pause_days: medication.pause_days || 0,
        total_period_months: medication.total_period_months || 3,
        notes: medication.notes,
        is_active: true
      };
      
      logger.debug('[FamilyService] Payload para medications_v2:', JSON.stringify(payload, null, 2));
      
      const data = await supabaseRestRequest('medications_v2?select=id,member_id,updated_at', 'POST', payload);

      const createdMedication = Array.isArray(data) ? data[0] : data;
      logger.sensitive('[FamilyService] Medicação v2 adicionada:', createdMedication);
      
      // IMPORTANTE: Os medication_logs_v2 são criados automaticamente pelo trigger!
      logger.debug('[FamilyService] Trigger create_medication_logs_v2 deve criar os logs automaticamente');
      
      return createdMedication;
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao adicionar medicação v2:', error);
      throw new Error(`Erro ao adicionar medicação: ${error.message}`);
    }
  }

  // Deletar medicação (v2 - HARD DELETE)
  async deleteMedication(medicationId: string): Promise<void> {
    logger.sensitive('[FamilyService] Deletando medicação v2 permanentemente:', medicationId);
    
    try {
      // CORRIGIDO: DELETE permanente (não soft delete)
      // Os medication_logs_v2 serão deletados automaticamente por CASCADE
      await supabaseRestRequest(`medications_v2?id=eq.${medicationId}`, 'DELETE');
      logger.success('[FamilyService] Medicação v2 deletada permanentemente (CASCADE nos logs)');
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao deletar medicação v2:', error);
      throw new Error(`Erro ao deletar medicação: ${error.message}`);
    }
  }

  // Atualizar medicação (v2)
  async updateMedication(medicationId: string, medication: any): Promise<any> {
    logger.sensitive('[FamilyService] Atualizando medicação v2:', medicationId);
    
    try {
      // ATUALIZADO: Usar medications_v2 (dosage_type + quantity, não dosage)
      const data = await supabaseRestRequest(`medications_v2?id=eq.${medicationId}&select=id,member_id,updated_at`, 'PATCH', {
        remedy_name: medication.remedy_name,
        potency: medication.potency,
        dosage_type: medication.dosage_type,
        quantity: medication.quantity,
        times: medication.times,
        frequency: medication.frequency,
        start_date: medication.start_date,
        end_date: medication.end_date,
        usage_days: medication.usage_days,
        pause_days: medication.pause_days,
        total_period_months: medication.total_period_months,
        notes: medication.notes,
        is_active: medication.is_active
      });

      logger.sensitive('[FamilyService] Medicação v2 atualizada:', data);
      return Array.isArray(data) ? data[0] : data;
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao atualizar medicação v2:', error);
      throw new Error(`Erro ao atualizar medicação: ${error.message}`);
    }
  }

  // ========== REGISTROS MÉDICOS ==========

  // Buscar registros médicos de um membro (v1 - deprecated)
  async getMemberMedicalRecords(memberId: string): Promise<any[]> {
    logger.warn('[FamilyService] getMemberMedicalRecords(v1) desativado. Use getMemberMedicalRecordsV2(). Redirecionando automaticamente...', memberId);
    return this.getMemberMedicalRecordsV2(memberId);
  }

  // ✅ PARTE 4: Buscar registros médicos v2
  async getMemberMedicalRecordsV2(memberId: string): Promise<any[]> {
    logger.sensitive('[FamilyService] Buscando registros médicos do membro (v2):', memberId);
    
    try {
      const data = await supabaseRestRequest(
        `medical_records_v2?member_id=eq.${memberId}&order=created_at.desc&select=*`,
        'GET'
      );

      logger.debug('[FamilyService] Registros médicos encontrados (v2):', data?.length || 0);
      return data || [];
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao buscar registros médicos v2:', error);
      return [];
    }
  }

  // Adicionar registro médico
  async addMedicalRecord(memberId: string, record: any): Promise<any> {
    logger.warn('[FamilyService] addMedicalRecord(v1) desativado. Use registerMedicalRecordV2(). memberId=', memberId, 'payload=', record);
    throw new Error('FamilyService.addMedicalRecord(v1) desativado. Use registerMedicalRecordV2(memberId, event, gravidade, alopatic, description).');
  }

  // ✅ PARTE 4: Registrar evento médico usando função do banco (v2)
  async registerMedicalRecordV2(
    memberId: string,
    event: 'doenca' | 'medicamento_alopatico',
    gravidade?: 'leve' | 'moderado' | 'alto',
    alopatic?: string,
    description?: string
  ): Promise<string> {
    logger.sensitive('Registrando evento médico v2', { memberId, event, gravidade, alopatic, description });
    
    try {
      // Chamar função SQL register_medical_record_v2()
      const data = await runOnlineOrToast(async () => {
        const { data, error } = await supabase.rpc('register_medical_record_v2', {
          p_member_id: memberId,
          p_event: event,
          p_gravidade: gravidade || null,
          p_alopatic: alopatic || null,
          p_description: description || null
        });

        if (error) {
          logger.error('[FamilyService] Erro ao registrar evento médico:', error);
          throw error;
        }

        return data as string;
      });

      logger.sensitive('[FamilyService] Evento médico registrado com sucesso. ID:', data);
      return data;
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao registrar evento médico:', error);
      throw new Error(`Erro ao registrar evento médico: ${error.message}`);
    }
  }

  // ========== EVENTOS DE SAÚDE ==========

  // Buscar eventos de saúde de um membro
  async getMemberHealthEvents(memberId: string): Promise<any[]> {
    logger.sensitive('[FamilyService] Buscando eventos de saúde do membro:', memberId);
    
    try {
      const data = await supabaseRestRequest(
        `health_events?member_id=eq.${memberId}&order=created_at.desc&select=*`,
        'GET'
      );

      logger.debug('[FamilyService] Eventos de saúde encontrados:', data?.length || 0);
      return data || [];
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao buscar eventos de saúde:', error);
      return [];
    }
  }

  /**
   * Busca diário de saúde unificado (health_events ∪ unified_point_history)
   * via view public.health_journal_view com paginação e filtros opcionais
   */
  async getMemberHealthJournal(
    memberId: string,
    opts?: {
      limit?: number;
      offset?: number;
      type?: 'all' | 'positive' | 'negative';
      category?: string; // 'general' | 'treatment' | 'illness' | 'allopathic' | 'medical_record' | 'badge' | 'level'
      since?: string; // ISO date/time
      until?: string; // ISO date/time
    }
  ): Promise<any[]> {
    logger.sensitive('[FamilyService] Buscando diário unificado do membro:', memberId, opts);
    try {
      let endpoint = `health_journal_view?member_id=eq.${memberId}&order=created_at.desc&select=*`;

      if (opts?.limit !== undefined) endpoint += `&limit=${opts.limit}`;
      if (opts?.offset !== undefined) endpoint += `&offset=${opts.offset}`;
      if (opts?.type && opts.type !== 'all') endpoint += `&event_type=eq.${opts.type}`;
      if (opts?.category && opts.category !== 'all') endpoint += `&category=eq.${opts.category}`;
      if (opts?.since) endpoint += `&created_at=gte.${opts.since}`;
      if (opts?.until) endpoint += `&created_at=lte.${opts.until}`;

      const data = await supabaseRestRequest(endpoint, 'GET');
      logger.debug('[FamilyService] Diário unificado encontrado:', data?.length || 0);
      return data || [];
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao buscar diário unificado:', error);
      return [];
    }
  }

  // Adicionar evento de saúde
  async addHealthEvent(memberId: string, event: any): Promise<any> {
    logger.sensitive('[FamilyService] Adicionando evento de saúde para membro:', memberId);
    
    try {
      // Corrigir timezone: event_date deve ser a data BRT do momento do registro
      const now = new Date();
      const brt = new Date(now.getTime() - 3 * 60 * 60 * 1000);
      const eventDateBrt = brt.toISOString().split('T')[0];

      const data = await supabaseRestRequest('health_events?select=*', 'POST', {
        member_id: memberId,
        title: event.title || event.description || 'Evento de Saúde',
        description: event.description,
        event_type: event.event_type,
        category: event.category,
        severity: event.severity,
        event_date: event.event_date || eventDateBrt
      });

      logger.sensitive('[FamilyService] Evento de saúde adicionado:', data);
      return Array.isArray(data) ? data[0] : data;
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao adicionar evento de saúde:', error);
      throw new Error(`Erro ao adicionar evento de saúde: ${error.message}`);
    }
  }

  // ========== LEMBRETES ==========

  // Buscar lembretes ativos de um membro
  async getMemberReminders(memberId: string): Promise<any[]> {
    logger.sensitive('[FamilyService] Buscando lembretes do membro:', memberId);
    
    try {
      // Ocultar lembretes únicos já vencidos (reminder_date < hoje BRT)
      const now = new Date();
      const brt = new Date(now.getTime() - 3 * 60 * 60 * 1000);
      const today = brt.toISOString().split('T')[0];
      // Filtro OR: (reminder_date é null [recorrente]) OU (reminder_date >= hoje)
      const endpoint = `reminders?member_id=eq.${memberId}`
        + `&is_active=eq.true`
        + `&or=(reminder_date.is.null,reminder_date.gte.${today})`
        + `&order=created_at.desc&select=`
        + [
            'id',
            'title',
            'description',
            'reminder_date',
            'reminder_time',
            'days_of_week',
            'created_at'
          ].join(',');
      const data = await supabaseRestRequest(endpoint, 'GET');

      logger.debug('[FamilyService] Lembretes encontrados:', (data || []).length || 0);
      // Sem filtro por horário do mesmo dia: exibir no Log até o job diário limpar
      return data || [];
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao buscar lembretes:', error);
      return [];
    }
  }

  // Adicionar lembrete
  async addReminder(memberId: string, reminder: any): Promise<any> {
    logger.sensitive('[FamilyService] Adicionando lembrete para membro:', memberId);
    logger.debug('[FamilyService] Dados do lembrete:', reminder);
    
    try {
      const data = await supabaseRestRequest('reminders?select=id,title,description,reminder_date,reminder_time,days_of_week', 'POST', {
        member_id: memberId,
        title: reminder.title,
        description: reminder.description,
        reminder_date: reminder.reminder_date || null,
        reminder_time: reminder.reminder_time,
        days_of_week: reminder.days_of_week || null,
        is_active: reminder.is_active !== undefined ? reminder.is_active : true
      });

      logger.sensitive('[FamilyService] Lembrete adicionado:', data);
      return Array.isArray(data) ? data[0] : data;
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao adicionar lembrete:', error);
      throw new Error(`Erro ao adicionar lembrete: ${error.message}`);
    }
  }

  // Atualizar lembrete
  async updateReminder(reminderId: string, reminder: any): Promise<any> {
    logger.sensitive('[FamilyService] Atualizando lembrete:', reminderId);
    logger.debug('[FamilyService] Dados do lembrete (update):', reminder);
    try {
      const payload = {
        title: reminder.title,
        description: reminder.description ?? null,
        // Se for único, manter reminder_date e limpar days_of_week; se recorrente, o inverso
        reminder_date: reminder.reminder_date || null,
        reminder_time: reminder.reminder_time,
        days_of_week: reminder.days_of_week || null,
        is_active: reminder.is_active !== undefined ? reminder.is_active : true,
      };

      const data = await supabaseRestRequest(
        `reminders?id=eq.${reminderId}&select=id,title,description,reminder_date,reminder_time,days_of_week`,
        'PATCH',
        payload
      );

      logger.sensitive('[FamilyService] Lembrete atualizado:', data);
      return Array.isArray(data) ? data[0] : data;
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao atualizar lembrete:', error);
      throw new Error(`Erro ao atualizar lembrete: ${error.message}`);
    }
  }

  // Deletar lembrete
  async deleteReminder(reminderId: string): Promise<void> {
    logger.sensitive('[FamilyService] Deletando lembrete:', reminderId);
    
    try {
      await supabaseRestRequest(`reminders?id=eq.${reminderId}`, 'DELETE');
      logger.success('[FamilyService] Lembrete deletado com sucesso');
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao deletar lembrete:', error);
      throw new Error(`Erro ao deletar lembrete: ${error.message}`);
    }
  }

  /**
   * Registra que uma medicação foi tomada (v2)
   * Usa a função PostgreSQL register_dose_v2
   */
  async logMedicationTaken(
    medicationId: string,
    scheduledTime: string,
    status: 'taken' | 'missed' | 'late' = 'taken',
    takenAt?: string
  ): Promise<any> {
    logger.sensitive('[FamilyService] Registrando dose (v2):', medicationId);
    logger.debug('[FamilyService] ScheduledTime recebido:', scheduledTime);
    logger.debug('[FamilyService] Status sugerido pelo frontend:', status);
    
    try {
      // CORRIGIDO (18/10/2025 00:49): Extrair horário UTC e converter para BRT
      // scheduledTime vem como '2025-10-17T11:25:00.000Z' (UTC)
      // Precisamos extrair o horário BRT (08:25) para buscar o log correto
      let timeSlot: string;
      
      if (scheduledTime.includes('T')) {
        // É um timestamp UTC, extrair horário UTC e subtrair 3h para BRT
        const utcDate = new Date(scheduledTime);
        const brtOffset = -3 * 60 * 60 * 1000; // -3 horas
        const brtDate = new Date(utcDate.getTime() + brtOffset);
        const hours = brtDate.getUTCHours().toString().padStart(2, '0');
        const minutes = brtDate.getUTCMinutes().toString().padStart(2, '0');
        timeSlot = `${hours}:${minutes}`;
      } else {
        // Já é um horário, usar como está
        timeSlot = scheduledTime.substring(0, 5);
      }
      
      const timeSlotWithSeconds = timeSlot.length === 5 ? `${timeSlot}:00` : timeSlot;

      logger.debug('[FamilyService] Time slot BRT extraído:', timeSlotWithSeconds);

      // Determinar timestamp final que será enviado para o backend
      let takenAtISO = scheduledTime;
      if (takenAt) {
        const takenAtDate = new Date(takenAt);
        takenAtISO = Number.isNaN(takenAtDate.getTime()) ? scheduledTime : takenAtDate.toISOString();
      } else if (!scheduledTime.includes('T')) {
        const todayBRT = new Date();
        const year = todayBRT.getFullYear();
        const month = String(todayBRT.getMonth() + 1).padStart(2, '0');
        const day = String(todayBRT.getDate()).padStart(2, '0');
        const localDateTime = `${year}-${month}-${day}T${timeSlotWithSeconds}-03:00`;
        takenAtISO = new Date(localDateTime).toISOString();
      }

      // ATUALIZADO: Usar função PostgreSQL register_dose_v2
      const data = await runOnlineOrToast(async () => {
        const { data, error } = await supabase.rpc('register_dose_v2' as any, {
          p_medication_id: medicationId,
          p_time_slot: timeSlotWithSeconds,
          p_taken_at: takenAtISO
        });
        if (error) throw error;
        return data;
      });

      logger.success('[FamilyService] Dose registrada (v2):', data);
      // Se a função SQL retornou um objeto com success=false, tratar como erro
      if (data && typeof data === 'object' && data.success === false) {
        const errMsg = data.error || 'Falha ao registrar dose';
        logger.warn('[FamilyService] Função register_dose_v2 retornou falha:', errMsg);
        throw new Error(errMsg);
      }
      return data;
    } catch (error: any) {
      logger.error('[FamilyService] Erro ao registrar dose:', error);
      throw new Error(`Erro ao registrar dose: ${error.message}`);
    }
  }

  /**
   * Verifica se uma medicação já foi tomada hoje (v2)
   */
  async checkMedicationTakenToday(medicationId: string, scheduledTime: string): Promise<boolean> {
    logger.sensitive('[FamilyService] Verificando se medicação foi tomada hoje (v2):', medicationId);
    
    try {
      // CORRIGIDO (17/10/2025 23:23): Calcular data BRT manualmente (offset -3h)
      // Problema: toLocaleString com timezone está bugado e retorna data errada
      const now = new Date();
      const utcTime = now.getTime();
      const brtOffset = -3 * 60 * 60 * 1000; // -3 horas em millisegundos
      const brtTime = new Date(utcTime + brtOffset);
      const today = brtTime.toISOString().split('T')[0];
      
      logger.debug('[FamilyService] Data de hoje (BRT):', today);
      logger.debug('[FamilyService] Horário atual (UTC):', now.toISOString());
      logger.debug('[FamilyService] Horário atual (BRT):', brtTime.toISOString());
      
      // CORRIGIDO: Extrair time_slot do scheduledTime
      let timeSlot: string;
      if (scheduledTime.includes('T')) {
        const date = new Date(scheduledTime);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        timeSlot = `${hours}:${minutes}`;
      } else {
        timeSlot = scheduledTime.substring(0, 5);
      }
      
      logger.debug('[FamilyService] Buscando log para time_slot:', timeSlot);
      
      // ATUALIZADO: Usar medication_logs_v2
      const { data, error } = await supabase
        .from('medication_logs_v2' as any)
        .select('doses_detail, taken, taken_late')
        .eq('medication_id', medicationId)
        .eq('time_slot', timeSlot)
        .single();

      if (error) {
        logger.debug('[FamilyService] Log não encontrado, medicação não foi tomada');
        return false;
      }

      if (!data) {
        logger.debug('[FamilyService] Nenhum dado encontrado no medication_logs_v2');
        return false;
      }

      logger.debug('[FamilyService] Dados do log:', { taken: data.taken, taken_late: data.taken_late, doses_detail: data.doses_detail });

      // CORRIGIDO: doses_detail pode vir como string JSON
      let dosesDetail = data.doses_detail;
      if (typeof dosesDetail === 'string') {
        dosesDetail = JSON.parse(dosesDetail);
      }
      
      if (!dosesDetail || Object.keys(dosesDetail).length === 0) {
        logger.debug('[FamilyService] doses_detail está vazio ou null');
        return false;
      }
      
      // Verificar se já foi tomada hoje no JSONB doses_detail
      // CORRIGIDO (17/10/2025 10:49): doses_detail é um OBJETO, não array!
      // CORRIGIDO (17/10/2025 23:19): Converter scheduled_time UTC para BRT
      // Estrutura: {"1": {...}, "2": {...}, "6": {...}}
      let todayDose = null;
      
      if (import.meta.env.DEV) {
        console.log('📦 [FamilyService] doses_detail:', JSON.stringify(dosesDetail, null, 2));
      }
      
      if (dosesDetail && typeof dosesDetail === 'object') {
        // Iterar sobre as chaves do objeto
        for (const key in dosesDetail) {
          const dose = dosesDetail[key];
          
          // Converter scheduled_time UTC para BRT antes de comparar (offset -3h)
          const scheduledUTC = new Date(dose.scheduled_time);
          const scheduledBRTTime = scheduledUTC.getTime() + brtOffset;
          const scheduledBRT = new Date(scheduledBRTTime);
          const doseDate = scheduledBRT.toISOString().split('T')[0];
          
          if (import.meta.env.DEV) {
            console.log(`🔍 [FamilyService] Dose #${key}: scheduled=${dose.scheduled_time}, doseDate=${doseDate}, status=${dose.status}, taken_at=${dose.taken_at}, today=${today}`);
          }
          
          // CORRIGIDO (20/10/2025 21:42): Também travar botão para doses perdidas que foram clicadas
          // Se o usuário clicou fora do prazo (missed com taken_at preenchido), o botão deve ficar travado
          if (doseDate === today && (dose.status === 'taken' || dose.status === 'taken_late' || (dose.status === 'missed' && dose.taken_at))) {
            if (import.meta.env.DEV) {
              console.log(`✅ [FamilyService] ENCONTROU dose de hoje! Dose #${key} (status: ${dose.status})`);
            }
            todayDose = dose;
            break;
          }
        }
      }

      // ANTES: const wasTaken = !!todayDose || data.taken > 0 || data.taken_late > 0;
      // PROBLEMA 1: Verificava contadores globais, travando botão para sempre
      // PROBLEMA 2: doses_detail é OBJETO, não array
      // PROBLEMA 3: Timezone UTC vs BRT causava bug após 21:00
      // DEPOIS: Verifica apenas se existe dose de HOJE no JSONB (objeto) em BRT
      const wasTaken = !!todayDose;
      if (import.meta.env.DEV) {
        console.log(`${wasTaken ? '✅' : '❌'} [FamilyService] Medicação ${wasTaken ? 'já foi' : 'não foi'} tomada hoje (${today})`);
      }
      
      return wasTaken;
    } catch (error: any) {
      console.error('❌ [FamilyService] Erro ao verificar medicação:', error);
      return false;
    }
  }
}

export default FamilyService;
